import { Link } from "react-router-dom";

import { useMe } from "@/api/auth";
import { useBalance } from "@/api/balance";
import { useOrders } from "@/api/orders";
import { useTransactions } from "@/api/transactions";
import { formatMsk } from "@/lib/datetime";
import { useT } from "@/lib/i18n";

/**
 * Fresh dashboard: light, airy hub with KPI stats, quick-action grid,
 * and side-by-side recent orders + transactions feeds. Uses the
 * `.fresh-*` CSS palette defined in index.css.
 */
export function HomeFresh() {
  const me = useMe();
  const balance = useBalance();
  const orders = useOrders({ offset: 0, limit: 5 });
  const tx = useTransactions({ offset: 0, limit: 5 });
  const t = useT();

  const balanceStr = balance.data?.balance ?? me.data?.balance?.toFixed(4);
  const greeting = `${greetingFor(new Date(), t)}, ${
    me.data?.username || me.data?.login || t("home.greeting.fallback")
  }`;

  const totalOrders = orders.data?.total ?? 0;
  const lastTopup = tx.data?.items.find((t) => isCredit(t.type));

  return (
    <div className="space-y-7">
      <section>
        <h1 style={{
          fontSize: 26,
          fontWeight: 700,
          color: "var(--color-text)",
          letterSpacing: "-0.01em",
        }}>
          {greeting}
        </h1>
        <p style={{ color: "var(--color-text-muted)", marginTop: 4 }}>
          {t("home.subtitle")}
        </p>
      </section>

      <section className="fresh-stat-grid">
        <div className="fresh-stat">
          <div className="fresh-stat-label">{t("home.kpi.balance")}</div>
          <div className="fresh-stat-value">${formatBalance(balanceStr)}</div>
          <div className="fresh-stat-trend muted">{t("home.kpi.balance.live")}</div>
        </div>
        <div className="fresh-stat">
          <div className="fresh-stat-label">{t("home.kpi.orders")}</div>
          <div className="fresh-stat-value">{totalOrders.toLocaleString()}</div>
          <div className="fresh-stat-trend muted">{t("home.kpi.orders.alltime")}</div>
        </div>
        <div className="fresh-stat">
          <div className="fresh-stat-label">{t("home.kpi.last_topup")}</div>
          <div className="fresh-stat-value">
            {lastTopup ? `+${lastTopup.amount.toFixed(2)}` : "-"}
          </div>
          <div className="fresh-stat-trend muted">
            {lastTopup?.date ? formatDate(lastTopup.date) : t("home.kpi.last_topup.none")}
          </div>
        </div>
        <div className="fresh-stat">
          <div className="fresh-stat-label">{t("home.kpi.account")}</div>
          <div className="fresh-stat-value" style={{ fontSize: 22 }}>
            {me.data?.username || me.data?.login || "-"}
          </div>
          <div className="fresh-stat-trend muted">#{me.data?.id}</div>
        </div>
      </section>

      <section>
        <h2 className="fresh-card-title" style={{ marginBottom: 12 }}>
          {t("home.quick.title")}
        </h2>
        <div className="fresh-action-grid">
          <Link to="/explore" className="fresh-action indigo">
            <span className="fresh-action-icon">{IconExplore}</span>
            <div className="fresh-action-title">{t("home.action.explore")}</div>
            <div className="fresh-action-desc">{t("home.action.explore.desc")}</div>
          </Link>
          <Link to="/steam-gifts" className="fresh-action violet">
            <span className="fresh-action-icon">{IconGamepad}</span>
            <div className="fresh-action-title">{t("home.action.steam_gifts")}</div>
            <div className="fresh-action-desc">{t("home.action.steam_gifts.desc")}</div>
          </Link>
          <Link to="/steam-topup" className="fresh-action green">
            <span className="fresh-action-icon">{IconWallet}</span>
            <div className="fresh-action-title">{t("home.action.steam_topup")}</div>
            <div className="fresh-action-desc">{t("home.action.steam_topup.desc")}</div>
          </Link>
          <Link to="/balance" className="fresh-action amber">
            <span className="fresh-action-icon">{IconPlus}</span>
            <div className="fresh-action-title">{t("home.action.balance")}</div>
            <div className="fresh-action-desc">{t("home.action.balance.desc")}</div>
          </Link>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="fresh-card">
          <div className="fresh-card-head">
            <h2 className="fresh-card-title">{t("home.recent.orders")}</h2>
            <Link to="/orders" className="fresh-card-link">{t("home.recent.view_all")}</Link>
          </div>
          {orders.isLoading && (
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {t("common.loading")}
            </div>
          )}
          {orders.data?.items.length === 0 && (
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {t("home.recent.no_orders")}{" "}
              <Link to="/explore" className="fresh-card-link">{t("home.recent.start_exploring")}</Link>
            </div>
          )}
          {orders.data?.items.map((o) => {
            const cls =
              o.status === 2 ? "green" :
              o.status === 7 ? "rose" :
              o.status === 10 ? "indigo" : "amber";
            return (
              <Link
                key={o.custom_id}
                to={`/orders/${o.custom_id}`}
                className="fresh-feed-row"
                style={{ textDecoration: "none" }}
              >
                <span className={`fresh-feed-icon ${cls}`}>{IconOrder}</span>
                <div className="fresh-feed-body">
                  <div className="fresh-feed-title">
                    {o.product || "-"}
                  </div>
                  <div className="fresh-feed-sub">
                    {o.status_message}{o.date ? ` · ${formatDate(o.date)}` : ""}
                  </div>
                </div>
                <span className="fresh-feed-amount neg">
                  −{(o.total_price ?? 0).toFixed(2)}
                </span>
              </Link>
            );
          })}
        </section>

        <section className="fresh-card">
          <div className="fresh-card-head">
            <h2 className="fresh-card-title">{t("home.recent.movements")}</h2>
            <Link to="/transactions" className="fresh-card-link">{t("home.recent.view_all")}</Link>
          </div>
          {tx.isLoading && (
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {t("common.loading")}
            </div>
          )}
          {tx.data?.items.length === 0 && (
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {t("home.recent.no_transactions")}
            </div>
          )}
          {tx.data?.items.map((row) => {
            const isPos = isCredit(row.type);
            const mag = Math.abs(row.amount);
            return (
              <div key={row.id} className="fresh-feed-row">
                <span className={`fresh-feed-icon ${isPos ? "green" : "rose"}`}>
                  {isPos ? IconArrowDown : IconArrowUp}
                </span>
                <div className="fresh-feed-body">
                  <div className="fresh-feed-title">
                    {humanType(row.type, t)}
                  </div>
                  <div className="fresh-feed-sub">
                    {row.date ? formatDate(row.date) : ""}
                  </div>
                </div>
                <span className={`fresh-feed-amount ${isPos ? "pos" : "neg"}`}>
                  {isPos ? "+" : "−"}
                  {mag.toFixed(2)}
                </span>
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}

function greetingFor(d: Date, t: (key: string) => string): string {
  const h = d.getHours();
  if (h < 5) return t("nav.greeting.night");
  if (h < 12) return t("nav.greeting.morning");
  if (h < 18) return t("nav.greeting.afternoon");
  return t("nav.greeting.evening");
}

function formatBalance(v: string | undefined): string {
  if (!v) return "-";
  const n = Number(v);
  if (Number.isFinite(n)) return n.toFixed(2);
  return v;
}

function formatDate(iso: string): string {
  // Always render in MSK so two operators in different timezones see
  // the same value the DB stores. `formatMsk` returns
  // `YYYY-MM-DD HH:MM`; truncate to month-day for the home stat lines.
  return formatMsk(iso).slice(5).replace(" ", " ");
}

function isCredit(type: string | null | undefined): boolean {
  // `amount` is always stored positive; sign comes from the type.
  return type === "topup" || type === "admin_topup" || type === "refund";
}

function humanType(
  type: string | null | undefined,
  t: (key: string) => string,
): string {
  switch (type) {
    case "topup": return t("tx.type.topup");
    case "purchase": return t("tx.type.purchase");
    case "refund": return t("tx.type.refund");
    case "admin_topup": return t("tx.type.admin_topup");
    case "admin_debit": return t("tx.type.admin_debit");
    default: return type || "-";
  }
}

/* ------------------------- Inline icons ------------------------- */

const IconExplore = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);
const IconGamepad = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="11" x2="10" y2="11" /><line x1="8" y1="9" x2="8" y2="13" />
    <line x1="15" y1="12" x2="15.01" y2="12" /><line x1="18" y1="10" x2="18.01" y2="10" />
    <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258A4 4 0 0 0 17.32 5z" />
  </svg>
);
const IconWallet = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
  </svg>
);
const IconPlus = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconOrder = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);
const IconArrowUp = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
  </svg>
);
const IconArrowDown = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
  </svg>
);
